/* assets/littleg-upload-notice-contracts.js — LITTLEG-ACTION-ENGINE-06
 * Canonical owner: LittleG Action Object normalization, Upload deadline DOM contract and F01→F02 Notice-derived handoff.
 * Upload integration: augments parse/packing responses and contributes confirm payload through the transport registry.
 * F02 integration: after a confirmed Notice reaches Records, derives and persists Route Readiness only as that Notice record's private child.
 * F02 evidence comes only from the authenticated no-store Family Route Readiness brief through LittleGUploadTransport.
 * A brief refresh may enrich unresolved evidence but must never downgrade an existing trusted route fact or verified entrance/last-500m owner.
 * recognition_state and guardian_state values are preserved, including guardian_state="removed".
 * Boundary: no OCR fabrication, direct fetch, route-duration fabrication, Today/F03 projection, map UI, reminder permission, navigation, GFun, checkout or formal report claim.
 */
(function littleGActionEngine(window, document) {
  'use strict';

  var VERSION = '2026-08-08-littleg-action-engine-06';
  var HOST_SELECTOR = '[data-upload-notice-contracts]';
  var ROOT_SELECTOR = '[data-littleg-upload-workspace]';
  var F02_SELECTOR = '[data-f02-route-readiness]';
  var ACTION_TYPES = ['notice', 'checklist', 'deadline', 'activity', 'city_mission'];
  var SOURCE_TYPES = ['camera', 'photo_library', 'pdf', 'manual', 'partner', 'qr_code', 'template'];
  var PROCESSING_STATES = ['draft', 'queued', 'processing', 'needs_confirmation', 'failed', 'ready'];
  var COMPLETION_STATES = ['not_started', 'in_progress', 'completed', 'guardian_confirmed'];
  var RECOGNITION_STATES = ['clear', 'needs_confirmation', 'uncertain', 'not_found'];
  var GUARDIAN_STATES = ['pending', 'confirmed', 'manually_edited', 'removed'];
  var SHAREABILITY = ['private', 'guardian_shareable', 'public_partner_content'];
  var DEADLINE_KINDS = ['reply', 'payment'];
  var transportBound = null;
  var currentAction = null;
  var activeHost = null;
  var briefRequested = Object.create(null);
  var routeSyncTimer = 0;

  function clean(value) { return String(value == null ? '' : value).trim(); }
  function clone(value) { try { return JSON.parse(JSON.stringify(value)); } catch (_) { return value; } }
  function pick(value, allowed, fallback) { return allowed.indexOf(clean(value)) >= 0 ? clean(value) : fallback; }
  function splitItems(value) {
    var seen = {};
    return clean(value).split(/[\n,，、;；|]+/).map(clean).filter(function (item) {
      var key = item.toLowerCase();
      if (!item || seen[key]) return false;
      seen[key] = true;
      return true;
    });
  }
  function makeId(prefix) {
    return (prefix || 'action') + '_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
  }
  function sourceType(value) {
    var normalized = clean(value).toLowerCase();
    if (normalized === 'library' || normalized === 'photo' || normalized === 'image') normalized = 'photo_library';
    if (normalized === 'camera_capture') normalized = 'camera';
    return pick(normalized, SOURCE_TYPES, 'manual');
  }
  function actionItem(raw, fallbackId, source) {
    if (typeof raw === 'string') raw = { name: raw };
    raw = raw && typeof raw === 'object' ? raw : {};
    var label = clean(raw.label || raw.name || raw.item || raw.title);
    if (!label) return null;
    return {
      item_id: clean(raw.item_id || raw.id || fallbackId) || makeId('item'),
      label: label,
      checked: raw.checked === true,
      source: pick(raw.source || source, ['required', 'suggested', 'guardian'], source || 'guardian'),
      recognition_state: pick(raw.recognition_state, RECOGNITION_STATES, 'needs_confirmation'),
      guardian_state: pick(raw.guardian_state, GUARDIAN_STATES, 'pending')
    };
  }
  function itemList(value, source) {
    var list = Array.isArray(value) ? value : splitItems(value);
    return list.map(function (item, index) {
      return actionItem(item, source + '_' + index, source);
    }).filter(Boolean);
  }
  function blankAction(seed) {
    seed = seed || {};
    return {
      action_id: clean(seed.action_id) || makeId('lga'),
      action_type: pick(seed.action_type, ACTION_TYPES, 'notice'),
      source_type: sourceType(seed.source_type),
      child_profile_id: clean(seed.child_profile_id) || null,
      title: clean(seed.title),
      event_at: clean(seed.event_at) || null,
      due_at: clean(seed.due_at) || null,
      place: clean(seed.place) || null,
      required_items: itemList(seed.required_items, 'required'),
      suggested_items: itemList(seed.suggested_items, 'suggested'),
      guardian_items: itemList(seed.guardian_items || seed.custom_items, 'guardian'),
      processing_state: pick(seed.processing_state, PROCESSING_STATES, 'draft'),
      completion_state: pick(seed.completion_state, COMPLETION_STATES, 'not_started'),
      recognition_state: pick(seed.recognition_state, RECOGNITION_STATES, 'needs_confirmation'),
      guardian_state: pick(seed.guardian_state, GUARDIAN_STATES, 'pending'),
      shareability: pick(seed.shareability, SHAREABILITY, seed.source_type === 'partner' ? 'public_partner_content' : 'private')
    };
  }
  function fieldValue(fields, keys) {
    var values = Array.isArray(fields) ? fields : [];
    var normalizedKeys = keys.map(function (key) { return clean(key).toLowerCase(); });
    for (var index = 0; index < normalizedKeys.length; index += 1) {
      var field = values.find(function (candidate) {
        if (!candidate || candidate.guardian_state === 'removed') return false;
        return clean(candidate.key || candidate.name || candidate.field).toLowerCase() === normalizedKeys[index];
      });
      if (field) return clean(field.value == null ? field.text : field.value);
    }
    return '';
  }
  function noticeTarget(response) {
    if (!response || typeof response !== 'object') return null;
    if (response.data && response.data.notice && typeof response.data.notice === 'object') return response.data.notice;
    if (response.notice && typeof response.notice === 'object') return response.notice;
    if (response.parsed_notice && typeof response.parsed_notice === 'object') return response.parsed_notice;
    if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) return response.data;
    return response;
  }
  function normalizeDeadline(raw, fallbackKind) {
    if (!raw) return null;
    if (typeof raw === 'string') raw = { due_at: raw, kind: fallbackKind };
    var due = clean(raw.due_at || raw.deadline || raw.value || raw.datetime);
    if (!due) return null;
    var kind = pick(raw.kind || raw.type || fallbackKind, DEADLINE_KINDS, 'reply');
    return {
      kind: kind,
      label: clean(raw.label) || (kind === 'payment' ? '付款期限' : '回條／回覆期限'),
      due_at: due,
      source_excerpt: clean(raw.source_excerpt || raw.excerpt),
      guardian_state: pick(raw.guardian_state, GUARDIAN_STATES, 'pending')
    };
  }

  function deadlineInput(kind, host) {
    var target = host || activeHost || document;
    return target && target.querySelector
      ? target.querySelector('[data-notice-deadline="' + pick(kind, DEADLINE_KINDS, 'reply') + '"]')
      : null;
  }
  function getDeadline(kind, host) {
    var safeKind = pick(kind, DEADLINE_KINDS, 'reply');
    var input = deadlineInput(safeKind, host);
    var due = clean(input && input.value);
    return due
      ? normalizeDeadline({ kind: safeKind, due_at: due, guardian_state: 'manually_edited' }, safeKind)
      : null;
  }
  function getAllDeadlines(host) {
    return DEADLINE_KINDS.map(function (kind) { return getDeadline(kind, host); }).filter(Boolean);
  }
  function setDeadline(kind, value, options) {
    options = options || {};
    var safeKind = pick(kind, DEADLINE_KINDS, 'reply');
    var input = deadlineInput(safeKind, options.host);
    if (!input) return false;
    var next = clean(value);
    if (options.overwrite === false && clean(input.value)) return false;
    var changed = clean(input.value) !== next;
    input.value = next;
    if (changed && options.emit !== false) {
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.dispatchEvent(new CustomEvent('littleg:notice-deadline-change', {
        bubbles: true,
        detail: { kind: safeKind, due_at: next, source: clean(options.source) || 'owner_api' }
      }));
    }
    return changed;
  }
  function restoreDeadlines(deadlines, options) {
    var values = Array.isArray(deadlines) ? deadlines : [];
    values.forEach(function (deadline) {
      if (!deadline) return;
      setDeadline(deadline.kind, deadline.due_at, {
        host: options && options.host,
        overwrite: true,
        emit: !(options && options.emit === false),
        source: options && options.source || 'restore'
      });
    });
    return getAllDeadlines(options && options.host);
  }

  function uiDeadlines(host) { return getAllDeadlines(host); }
  function augmentNotice(notice, host) {
    var source = clone(notice || {}) || {};
    var fields = Array.isArray(source.fields)
      ? source.fields.map(function (field) { return Object.assign({}, field); })
      : [];
    var deadlines = (Array.isArray(source.deadlines) ? source.deadlines : []).map(function (item) {
      return normalizeDeadline(item, clean(item && item.kind));
    }).filter(Boolean);
    var replyValue = fieldValue(fields, ['reply_deadline', 'reply_slip_deadline', 'response_deadline']);
    var paymentValue = fieldValue(fields, ['payment_deadline', 'fee_deadline']);
    if (replyValue) deadlines.push(normalizeDeadline(replyValue, 'reply'));
    if (paymentValue) deadlines.push(normalizeDeadline(paymentValue, 'payment'));
    uiDeadlines(host).forEach(function (deadline) {
      deadlines = deadlines.filter(function (item) { return item.kind !== deadline.kind; });
      deadlines.push(deadline);
    });
    var seen = {};
    deadlines = deadlines.filter(function (deadline) {
      var key = deadline.kind + '|' + deadline.due_at;
      if (!deadline.due_at || seen[key]) return false;
      seen[key] = true;
      return true;
    });
    var bringItems = Array.isArray(source.bring_items)
      ? source.bring_items
      : splitItems(fieldValue(fields, ['bring_items', 'required_items', 'items', 'materials']));
    source.fields = fields;
    source.deadlines = deadlines;
    source.bring_items = bringItems;
    source.schema_version = clean(source.schema_version) || 'littleg_notice_review_v1';
    source.action = fromNotice(source, {
      source_type: source.source_type || (currentAction && currentAction.source_type),
      child_profile_id: source.child_profile_id || (currentAction && currentAction.child_profile_id),
      action_id: source.action_id || (currentAction && currentAction.action_id)
    });
    source.action_id = source.action.action_id;
    return source;
  }
  function fromNotice(notice, options) {
    notice = notice || {};
    options = options || {};
    var fields = Array.isArray(notice.fields) ? notice.fields : [];
    var deadlines = Array.isArray(notice.deadlines) ? notice.deadlines : [];
    var due = deadlines.length ? clean(deadlines[0].due_at) : fieldValue(fields, ['reply_deadline', 'payment_deadline']);
    var required = notice.required_items || notice.bring_items || fieldValue(fields, ['bring_items', 'required_items', 'items', 'materials']);
    return blankAction({
      action_id: options.action_id || notice.action_id || notice.request_id,
      action_type: options.action_type || (notice.packing_list ? 'checklist' : 'notice'),
      source_type: options.source_type || notice.source_type || 'manual',
      child_profile_id: options.child_profile_id || notice.child_profile_id,
      title: notice.title || notice.activity_name || fieldValue(fields, ['activity_name', 'event_name', 'title']),
      event_at: notice.event_at || notice.activity_date || fieldValue(fields, ['activity_date', 'event_at', 'date']),
      due_at: notice.due_at || due,
      place: notice.place || notice.location || fieldValue(fields, ['location', 'place']),
      required_items: required,
      suggested_items: notice.suggested_items,
      guardian_items: notice.guardian_items || notice.custom_items,
      processing_state: notice.processing_state || 'needs_confirmation',
      completion_state: notice.completion_state || 'not_started',
      recognition_state: notice.recognition_state || 'needs_confirmation',
      guardian_state: notice.guardian_state || 'pending',
      shareability: notice.shareability
    });
  }
  function fromCityMission(mission, options) {
    mission = mission || {};
    options = options || {};
    return blankAction({
      action_id: options.action_id || mission.action_id || mission.mission_id || mission.record_id,
      action_type: 'city_mission',
      source_type: options.source_type || mission.source_type || 'template',
      child_profile_id: options.child_profile_id || mission.child_profile_id,
      title: mission.title || mission.mission_title || mission.generator_name,
      event_at: mission.event_at,
      due_at: mission.due_at,
      place: mission.place || mission.location || mission.district,
      required_items: mission.clues || mission.required_items,
      suggested_items: mission.suggested_items,
      guardian_items: mission.guardian_items,
      processing_state: mission.processing_state || 'ready',
      completion_state: mission.completion_state || (mission.completed ? 'completed' : 'not_started'),
      recognition_state: mission.recognition_state || 'clear',
      guardian_state: mission.guardian_state || 'pending',
      shareability: mission.shareability || 'private'
    });
  }
  function lifecycleForUpload(state, action) {
    var next = blankAction(action || {});
    if (state === 'loading') next.processing_state = 'processing';
    else if (state === 'error') next.processing_state = 'failed';
    else if (state === 'offline') next.processing_state = 'queued';
    else if (state === 'review') next.processing_state = 'needs_confirmation';
    else if (['packing', 'prep', 'confirm', 'records'].indexOf(state) >= 0) next.processing_state = 'ready';
    else next.processing_state = 'draft';
    if (state === 'packing') next.action_type = 'checklist';
    if (state === 'prep') next.completion_state = 'in_progress';
    if (state === 'confirm') next.completion_state = 'completed';
    if (state === 'records') {
      next.completion_state = 'guardian_confirmed';
      next.guardian_state = 'confirmed';
      if (next.shareability === 'private') next.shareability = 'guardian_shareable';
    }
    return next;
  }
  function emit(action, source) {
    currentAction = blankAction(action || {});
    try {
      window.dispatchEvent(new CustomEvent('littleg:action:updated', {
        detail: { action: clone(currentAction), source: source || 'littleg-action-engine', version: VERSION }
      }));
    } catch (_) {}
    return currentAction;
  }
  function syncUiFromNotice(host, notice) {
    (notice && Array.isArray(notice.deadlines) ? notice.deadlines : []).forEach(function (deadline) {
      setDeadline(deadline.kind, deadline.due_at, {
        host: host,
        overwrite: false,
        emit: false,
        source: 'notice_response'
      });
    });
  }
  function applyResponse(response, host) {
    var copy = clone(response);
    var target = noticeTarget(copy);
    if (!target) return response;
    var augmented = augmentNotice(target, host);
    if (copy.data && copy.data.notice) copy.data.notice = augmented;
    else if (copy.notice) copy.notice = augmented;
    else if (copy.parsed_notice) copy.parsed_notice = augmented;
    else if (copy.data && target === copy.data) copy.data = augmented;
    else copy = augmented;
    syncUiFromNotice(host, augmented);
    emit(augmented.action, 'notice-response');
    return copy;
  }
  function registerConfirmContributor(host) {
    var transport = window.LittleGUploadTransport;
    if (!transport || typeof transport.registerConfirmContributor !== 'function') return;
    transport.registerConfirmContributor('notice-action', function (payload) {
      var next = Object.assign({}, payload || {});
      next.notice = augmentNotice(next.notice || {}, host);
      next.notice.action = lifecycleForUpload('confirm', next.notice.action);
      emit(next.notice.action, 'confirmNotice');
      return next;
    }, 30);
  }
  function wrapParseAndPacking(host) {
    var transport = window.LittleGUploadTransport;
    if (!transport || transport === transportBound) return;
    if (typeof transport.parseNotice === 'function') {
      var parse = transport.parseNotice;
      transport.parseNotice = function parseAction(input) {
        var source = sourceType(input && input.source);
        currentAction = blankAction({
          action_id: currentAction && currentAction.action_id,
          action_type: 'notice',
          source_type: source,
          processing_state: 'processing'
        });
        emit(currentAction, 'capture');
        return Promise.resolve(parse.call(transport, input)).then(function (response) {
          return applyResponse(response, host);
        });
      };
    }
    if (typeof transport.generatePackingList === 'function') {
      var generate = transport.generatePackingList;
      transport.generatePackingList = function generateAction(input) {
        var payload = Object.assign({}, input || {});
        payload.notice = augmentNotice(payload.notice || {}, host);
        payload.notice.action = lifecycleForUpload('packing', payload.notice.action);
        emit(payload.notice.action, 'generatePackingList');
        return generate.call(transport, payload);
      };
    }
    registerConfirmContributor(host);
    transportBound = transport;
  }

  function familyDataOwner() {
    var owner = window.LittleGFamilyData;
    return owner && typeof owner.listRecords === 'function' && typeof owner.saveRecord === 'function' ? owner : null;
  }
  function recordIdentity(record) {
    return clean(record && (record.record_id || record.result_id || record.card_id || record.id));
  }
  function routeRecord(root) {
    var owner = familyDataOwner();
    if (!owner || !root) return null;
    var id = clean(root.getAttribute('data-record-id'));
    if (!id) {
      var node = root.querySelector('[data-record-id]');
      id = clean(node && node.textContent);
    }
    if (!id || id === '—') return null;
    return owner.listRecords().find(function (record) {
      return record && record.record_type === 'notice' && recordIdentity(record) === id;
    }) || null;
  }
  function noticeFromRecord(record) {
    return record && record.notice && typeof record.notice === 'object' ? record.notice : {};
  }
  function destinationFromRecord(record) {
    var notice = noticeFromRecord(record);
    var fields = Array.isArray(notice.fields) ? notice.fields : [];
    return clean(
      notice.destination || notice.location || notice.place ||
      fieldValue(fields, ['meeting_place', 'location', 'activity_location', 'venue', 'place', 'destination', 'address'])
    ).slice(0, 240);
  }
  function normalizeExactDate(value) {
    var text = clean(value);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return '';
    return Number.isFinite(Date.parse(text + 'T00:00:00+08:00')) ? text : '';
  }
  function normalizeExactTime(value) {
    var text = clean(value);
    var match = text.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
    return match ? match[1] + ':' + match[2] : '';
  }
  function normalizeExactDateTime(value) {
    var text = clean(value);
    if (!/^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):[0-5]\d/.test(text)) return '';
    if (/(Z|[+-]\d{2}:\d{2})$/i.test(text)) return Number.isFinite(Date.parse(text)) ? text : '';
    var compact = text.match(/^(\d{4}-\d{2}-\d{2}T[0-2]\d:[0-5]\d)(?::[0-5]\d)?$/);
    if (!compact) return '';
    var local = compact[1] + ':00+08:00';
    return Number.isFinite(Date.parse(local)) ? local : '';
  }
  function arrivalTargetFromRecord(record) {
    var notice = noticeFromRecord(record);
    var fields = Array.isArray(notice.fields) ? notice.fields : [];
    var full = normalizeExactDateTime(
      notice.arrival_target || notice.event_at || notice.activity_datetime ||
      fieldValue(fields, ['arrival_target', 'arrival_at', 'event_at', 'activity_datetime'])
    );
    if (full) return full;
    var date = normalizeExactDate(notice.activity_date || fieldValue(fields, ['activity_date', 'event_date', 'date']));
    var time = normalizeExactTime(notice.activity_time || notice.meeting_time || notice.start_time || fieldValue(fields, ['meeting_time', 'activity_time', 'start_time', 'event_time', 'time']));
    return date && time ? date + 'T' + time + ':00+08:00' : null;
  }
  function childLabelFromRecord(record) {
    var notice = noticeFromRecord(record);
    var child = notice.child && typeof notice.child === 'object' ? notice.child : {};
    var label = clean(child.nickname_snapshot || child.nickname || record && record.child_nickname);
    if (label) return label.slice(0, 80);
    var binding = window.LittleGUploadChildBinding;
    var snapshot = binding && typeof binding.getSnapshot === 'function' ? binding.getSnapshot() : null;
    return clean(snapshot && snapshot.nickname_snapshot).slice(0, 80) || '—';
  }
  function packingCrossCheck(record) {
    var checklist = record && Array.isArray(record.checklist) ? record.checklist : [];
    var active = checklist.filter(function (item) { return item && item.removed !== true; });
    var total = active.length;
    var incomplete = active.filter(function (item) { return item.checked !== true; }).length;
    return { total: total, incomplete: incomplete, ready: total > 0 && incomplete === 0 };
  }
  function trustedRoute(model) {
    return model && model.route_fact && clean(model.route_fact.source) === 'trusted_route_provider';
  }
  function isVerified(component) {
    return component && clean(component.status) === 'verified';
  }
  function baseRouteReadiness(record) {
    var previous = record && record.route_readiness && typeof record.route_readiness === 'object' ? clone(record.route_readiness) : {};
    var trusted = trustedRoute(previous);
    var arrival = arrivalTargetFromRecord(record);
    var destination = destinationFromRecord(record);
    var previousDropoff = previous.dropoff && typeof previous.dropoff === 'object' ? previous.dropoff : {};
    var previousPickup = previous.pickup && typeof previous.pickup === 'object' ? previous.pickup : {};
    var previousPublic = previous.public_context && typeof previous.public_context === 'object' ? previous.public_context : {};
    var previousEntrance = previous.entrance && typeof previous.entrance === 'object' ? previous.entrance : {};
    var previousLast500 = previous.last_500m && typeof previous.last_500m === 'object' ? previous.last_500m : {};
    return {
      version: 1,
      notice_record_id: recordIdentity(record),
      destination_text: destination,
      arrival_target: arrival,
      departure_window: trusted ? clone(previous.departure_window || {}) : { recommended_at: null, leave_by: null },
      route_fact: trusted ? clone(previous.route_fact) : { duration_minutes: null, source: 'none', confidence: 'unknown' },
      entrance: {
        status: clean(previousEntrance.status) || 'unknown',
        label: clean(previousEntrance.label) || null,
        confidence: clean(previousEntrance.confidence) || 'unknown'
      },
      last_500m: {
        status: clean(previousLast500.status) || 'unknown',
        instruction: clean(previousLast500.instruction) || null,
        confidence: clean(previousLast500.confidence) || 'unknown'
      },
      dropoff: {
        guardian: clean(previousDropoff.guardian) || null,
        note: clean(previousDropoff.note) || null
      },
      pickup: {
        guardian: clean(previousPickup.guardian) || null,
        note: clean(previousPickup.note) || null
      },
      guardian_responsibility: clean(previous.guardian_responsibility) || null,
      packing_cross_check: packingCrossCheck(record),
      public_context: {
        data_state: clean(previousPublic.data_state) || 'unknown',
        traffic_state: clean(previousPublic.traffic_state) || 'unknown',
        weather_state: clean(previousPublic.weather_state) || 'unknown',
        holiday_state: clean(previousPublic.holiday_state) || 'unknown'
      },
      readiness_state: trusted
        ? clean(previous.readiness_state) || 'route_window_estimated'
        : (arrival ? 'arrival_deadline_set' : 'context_only'),
      precise_location_saved: false,
      updated_at: new Date().toISOString()
    };
  }
  function mergeBriefRouteReadiness(existing, incoming) {
    var previous = existing && typeof existing === 'object' ? clone(existing) : {};
    var next = incoming && typeof incoming === 'object' ? clone(incoming) : {};
    if (trustedRoute(previous)) {
      next.route_fact = clone(previous.route_fact);
      next.departure_window = clone(previous.departure_window || {});
      next.readiness_state = clean(previous.readiness_state) || 'route_window_estimated';
    } else {
      next.route_fact = { duration_minutes: null, source: 'none', confidence: 'unknown' };
      next.departure_window = { recommended_at: null, leave_by: null };
      next.readiness_state = clean(next.arrival_target) ? 'arrival_deadline_set' : 'context_only';
    }
    if (isVerified(previous.entrance)) next.entrance = clone(previous.entrance);
    if (isVerified(previous.last_500m)) next.last_500m = clone(previous.last_500m);
    next.precise_location_saved = false;
    next.updated_at = new Date().toISOString();
    return next;
  }
  function saveRouteReadiness(record, model) {
    var owner = familyDataOwner();
    if (!owner || !record) return null;
    return owner.saveRecord('notice', Object.assign({}, record, { route_readiness: model }));
  }
  function f02Host() { return document.querySelector(F02_SELECTOR); }
  function setF02Status(host, state) {
    if (!host) return;
    var safe = ['idle', 'loading', 'ready', 'needs_destination', 'unavailable', 'storage_unavailable'].indexOf(state) >= 0 ? state : 'unavailable';
    host.setAttribute('data-f02-state', safe);
    Array.prototype.forEach.call(host.querySelectorAll('[data-f02-status]'), function (node) {
      node.hidden = node.getAttribute('data-f02-status') !== safe;
    });
  }
  function attachF02Host(root) {
    var host = f02Host();
    var recordHost = root && root.querySelector('[data-packing-record]');
    if (!host || !recordHost) return host;
    if (host.parentNode !== recordHost) recordHost.appendChild(host);
    return host;
  }
  function renderF02(root, record) {
    var host = attachF02Host(root);
    if (!host || !record) return host;
    var model = record.route_readiness && typeof record.route_readiness === 'object'
      ? record.route_readiness
      : baseRouteReadiness(record);
    var packing = model.packing_cross_check || packingCrossCheck(record);
    var checked = Math.max(0, Number(packing.total || 0) - Number(packing.incomplete || 0));
    var values = {
      '[data-f02-destination]': clean(model.destination_text) || '—',
      '[data-f02-arrival]': clean(model.arrival_target) || '—',
      '[data-f02-child]': childLabelFromRecord(record),
      '[data-f02-packing]': checked + '/' + Number(packing.total || 0)
    };
    Object.keys(values).forEach(function (selector) {
      var node = host.querySelector(selector);
      if (node) node.textContent = values[selector];
    });
    var guardian = host.querySelector('[data-f02-guardian]');
    var dropoff = host.querySelector('[data-f02-dropoff-note]');
    var pickup = host.querySelector('[data-f02-pickup-note]');
    if (guardian && document.activeElement !== guardian) guardian.value = clean(model.guardian_responsibility || model.dropoff && model.dropoff.guardian || model.pickup && model.pickup.guardian);
    if (dropoff && document.activeElement !== dropoff) dropoff.value = clean(model.dropoff && model.dropoff.note);
    if (pickup && document.activeElement !== pickup) pickup.value = clean(model.pickup && model.pickup.note);
    host.setAttribute('data-f02-record-id', recordIdentity(record));
    host.setAttribute('data-f02-readiness-state', clean(model.readiness_state) || 'context_only');
    host.setAttribute('data-f02-route-fact-source', clean(model.route_fact && model.route_fact.source) || 'none');
    host.hidden = false;
    return host;
  }
  function routeRequest(model) {
    return {
      notice_record_id: clean(model.notice_record_id),
      destination_text: clean(model.destination_text),
      arrival_target: clean(model.arrival_target) || null,
      packing_cross_check: clone(model.packing_cross_check || {}),
      guardian_responsibility: clean(model.guardian_responsibility) || null,
      dropoff: clone(model.dropoff || {}),
      pickup: clone(model.pickup || {})
    };
  }
  async function refreshRouteReadinessBrief(root) {
    var record = routeRecord(root);
    var host = attachF02Host(root);
    if (!record || !host) {
      if (host) { host.hidden = false; setF02Status(host, 'storage_unavailable'); }
      return null;
    }
    var base = baseRouteReadiness(record);
    var saved = saveRouteReadiness(record, base) || record;
    renderF02(root, saved);
    if (!base.destination_text) {
      setF02Status(host, 'needs_destination');
      return saved;
    }
    var transport = window.LittleGUploadTransport;
    if (!transport || typeof transport.getRouteReadinessBrief !== 'function') {
      setF02Status(host, 'unavailable');
      return saved;
    }
    setF02Status(host, 'loading');
    try {
      var response = await transport.getRouteReadinessBrief(routeRequest(base));
      var returned = response && response.data && response.data.route_readiness;
      if (!returned || typeof returned !== 'object') throw new Error('route_readiness_missing');
      var latest = routeRecord(root) || saved;
      var existing = latest.route_readiness && typeof latest.route_readiness === 'object'
        ? latest.route_readiness
        : baseRouteReadiness(latest);
      var merged = mergeBriefRouteReadiness(existing, returned);
      var persisted = saveRouteReadiness(latest, merged) || latest;
      renderF02(root, persisted);
      setF02Status(host, 'ready');
      return persisted;
    } catch (error) {
      host.setAttribute('data-f02-brief-error', clean(error && (error.code || error.message)) || 'brief_unavailable');
      setF02Status(host, 'unavailable');
      return saved;
    }
  }
  function persistGuardianHandoff(root) {
    var record = routeRecord(root);
    var host = f02Host();
    if (!record || !host || host.hidden) return null;
    var model = baseRouteReadiness(record);
    var guardianNode = host.querySelector('[data-f02-guardian]');
    var dropoffNode = host.querySelector('[data-f02-dropoff-note]');
    var pickupNode = host.querySelector('[data-f02-pickup-note]');
    var guardian = clean(guardianNode && guardianNode.value).slice(0, 80);
    var dropoff = clean(dropoffNode && dropoffNode.value).slice(0, 240);
    var pickup = clean(pickupNode && pickupNode.value).slice(0, 240);
    model.guardian_responsibility = guardian || null;
    model.dropoff = { guardian: guardian || null, note: dropoff || null };
    model.pickup = { guardian: guardian || null, note: pickup || null };
    model.updated_at = new Date().toISOString();
    var saved = saveRouteReadiness(record, model);
    if (saved) renderF02(root, saved);
    return saved;
  }
  function bindF02Controls(root, host) {
    if (!root || !host || host.getAttribute('data-f02-bound') === VERSION) return;
    host.setAttribute('data-f02-bound', VERSION);
    var refresh = host.querySelector('[data-f02-refresh-public-context]');
    if (refresh) refresh.addEventListener('click', function () {
      persistGuardianHandoff(root);
      refreshRouteReadinessBrief(root);
    });
    ['[data-f02-guardian]', '[data-f02-dropoff-note]', '[data-f02-pickup-note]'].forEach(function (selector) {
      var input = host.querySelector(selector);
      if (input) input.addEventListener('change', function () { persistGuardianHandoff(root); });
    });
  }
  function hasBriefEvidence(model) {
    var context = model && model.public_context && typeof model.public_context === 'object' ? model.public_context : {};
    return ['data_state', 'traffic_state', 'weather_state', 'holiday_state'].some(function (key) {
      var value = clean(context[key]);
      return value && value !== 'unknown';
    });
  }
  function syncRouteReadiness(root, options) {
    options = options || {};
    var host = attachF02Host(root);
    if (!host || !root) return;
    if (clean(root.getAttribute('data-upload-state')) !== 'records') {
      host.hidden = true;
      return;
    }
    host.hidden = false;
    bindF02Controls(root, host);
    var record = routeRecord(root);
    if (!record) {
      setF02Status(host, 'storage_unavailable');
      return;
    }
    var model = baseRouteReadiness(record);
    var saved = saveRouteReadiness(record, model);
    if (!saved) {
      renderF02(root, record);
      setF02Status(host, 'storage_unavailable');
      return;
    }
    renderF02(root, saved);
    setF02Status(host, model.destination_text ? (hasBriefEvidence(model) ? 'ready' : 'idle') : 'needs_destination');
    var id = recordIdentity(saved);
    if (model.destination_text && (options.force === true || !briefRequested[id])) {
      briefRequested[id] = true;
      refreshRouteReadinessBrief(root);
    }
  }
  function scheduleRouteReadiness(root, options) {
    window.clearTimeout(routeSyncTimer);
    routeSyncTimer = window.setTimeout(function () { syncRouteReadiness(root, options); }, 0);
  }
  function hideRouteReadiness() {
    var host = f02Host();
    if (host) host.hidden = true;
  }

  function applyFocusedSurface(root) {
    var body = document.body;
    if (!body || !root) return;
    body.setAttribute('data-primary-action-surface', 'true');
    body.setAttribute('data-primary-action-verb', 'capture-understand-confirm-prepare-complete');
    root.setAttribute('data-action-engine-owner', 'assets/littleg-upload-notice-contracts.js');
    root.setAttribute('data-action-lifecycle', 'capture-understand-confirm-prepare-complete-record-reuse-share');
  }
  function bind(host) {
    if (!host || host.getAttribute('data-notice-contract-bound') === VERSION) return;
    host.setAttribute('data-notice-contract-bound', VERSION);
    activeHost = host;
    wrapParseAndPacking(host);
    registerConfirmContributor(host);
    var root = host.closest(ROOT_SELECTOR) || document.querySelector(ROOT_SELECTOR);
    applyFocusedSurface(root);
    currentAction = blankAction({
      action_id: root && root.dataset.actionId,
      action_type: 'notice',
      source_type: 'manual'
    });
    if (root) root.dataset.actionId = currentAction.action_id;
    emit(lifecycleForUpload(root && root.dataset.uploadState || 'empty', currentAction), 'boot');
    if (root && clean(root.dataset.uploadState) === 'records') scheduleRouteReadiness(root);
  }
  function boot(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll(HOST_SELECTOR), bind);
  }

  window.LittleGNoticeDeadlines = Object.freeze({
    version: VERSION,
    getInput: function (kind) { return deadlineInput(kind, activeHost); },
    get: function (kind) { return getDeadline(kind, activeHost); },
    getAll: function () { return getAllDeadlines(activeHost); },
    set: function (kind, value, options) {
      return setDeadline(kind, value, Object.assign({}, options || {}, { host: activeHost }));
    },
    restore: function (deadlines, options) {
      return restoreDeadlines(deadlines, Object.assign({}, options || {}, { host: activeHost }));
    }
  });

  window.LittleGActionEngine = {
    version: VERSION,
    actionTypes: ACTION_TYPES.slice(),
    sourceTypes: SOURCE_TYPES.slice(),
    create: blankAction,
    fromNotice: fromNotice,
    fromCityMission: fromCityMission,
    transitionUpload: lifecycleForUpload,
    getCurrent: function () { return clone(currentAction); },
    setCurrent: function (action, source) { return emit(action, source || 'manual'); }
  };

  document.addEventListener('littleg:upload-state-change', function (event) {
    var detail = event && event.detail || {};
    var state = detail.state || 'empty';
    emit(lifecycleForUpload(state, currentAction), 'upload-state-change');
    var root = event && event.target && event.target.closest
      ? event.target.closest(ROOT_SELECTOR)
      : document.querySelector(ROOT_SELECTOR);
    if (state === 'records' && root) scheduleRouteReadiness(root);
    else hideRouteReadiness();
  });
  window.addEventListener('littleg:preview-generated', function (event) {
    var detail = event && event.detail || {};
    if (detail.action_type === 'city_mission' || detail.lead_type === 'city_issue') {
      emit(fromCityMission(detail, { source_type: 'template' }), 'city-mission-preview');
    }
  });

  try {
    document.dispatchEvent(new CustomEvent('littleg:notice-deadline-owner-ready', {
      detail: { version: VERSION, kinds: DEADLINE_KINDS.slice(), f02_handoff: 'authenticated_notice_record_child_v1' }
    }));
  } catch (_) {}

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); }, { once: true });
  } else {
    boot(document);
  }
  document.addEventListener('shopify:section:load', function (event) { boot(event.target || document); });
})(window, document);